import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Printer, Mail, Home, Loader2, XCircle } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";

export default function ReceiptView() {
  const { token } = useParams();
  const [r, setR] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/receipts/${encodeURIComponent(token)}`)
      .then((res) => setR(res.data))
      .catch(() => setNotFound(true));
  }, [token]);

  const emailReceipt = async () => {
    setEmailBusy(true);
    try {
      await api.post(`/public/receipts/${encodeURIComponent(token)}/email`);
      toast.success("Receipt emailed to the registered email address.");
    } catch (e) { toast.error(errMsg(e)); } finally { setEmailBusy(false); }
  };

  if (notFound) {
    return (
      <div className="bg-white py-24 text-center" data-testid="receipt-not-found">
        <XCircle className="mx-auto h-12 w-12 text-red-400" strokeWidth={1.5} />
        <h1 className="mt-4 font-display text-2xl font-bold text-navy">Receipt not available</h1>
        <p className="mt-2 text-sm text-slate-500">This receipt link is invalid or the donation is not confirmed yet.</p>
        <Link to="/print-receipt" className="btn-primary mt-6 inline-flex !rounded-md !px-6 !py-2.5 text-sm">Find My Receipt</Link>
      </div>
    );
  }
  if (!r) {
    return <div className="bg-white py-24 text-center" data-testid="receipt-loading"><Loader2 className="mx-auto h-10 w-10 animate-spin text-saffron-600" /></div>;
  }

  const f = r.foundation || {};
  return (
    <div className="bg-navy-50/40 py-10" data-testid="receipt-page">
      <SEO title={`Receipt ${r.receipt_no}`} />
      <style>{`@media print { header, footer, .no-print { display: none !important; } body { background: white; } .receipt-card { box-shadow: none !important; border: 1px solid #ccc !important; } }`}</style>
      <div className="container-ssf max-w-3xl">
        <div className="receipt-card rounded-lg border border-gray-200 bg-white p-6 shadow-sm sm:p-10" data-testid="receipt-card">
          <div className="flex items-start justify-between gap-4 border-b-4 border-saffron-500 pb-5">
            <div>
              <h1 className="font-display text-xl font-bold tracking-wide text-navy sm:text-2xl" data-testid="receipt-foundation">{f.name || "SAHAJ SARATHI FOUNDATION"}</h1>
              {f.address && <p className="mt-1 text-xs text-slate-500">{f.address}</p>}
              <p className="mt-1 text-[11px] text-slate-400">{[f.udyam && `UDYAM: ${f.udyam}`, f.darpan && `NITI Darpan: ${f.darpan}`, f.tax_80g && `80G: ${f.tax_80g}`, f.tax_12a && `12A: ${f.tax_12a}`].filter(Boolean).join("  ·  ")}</p>
            </div>
            <div className="text-center">
              {r.qr && <img src={r.qr} alt="Scan to Verify Receipt" className="h-24 w-24" data-testid="receipt-qr" />}
              <p className="mt-1 text-[10px] font-semibold text-slate-500">Scan to Verify Receipt</p>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-end justify-between gap-2">
            <div>
              <p className="text-lg font-bold text-saffron-600">DONATION RECEIPT</p>
              <p className="mt-1 text-sm text-navy">Receipt No: <b data-testid="receipt-no">{r.receipt_no}</b></p>
              <p className="text-xs text-slate-500">Donation No: {r.don_no}</p>
            </div>
            <p className="text-sm text-navy">Date: <b>{r.date ? new Date(r.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</b></p>
          </div>

          <div className="mt-6">
            <p className="text-sm text-navy">Dear <b>{r.donor_name}</b>,</p>
            <p className="mt-2 text-[13px] leading-relaxed text-slate-600">
              Thank you for your generous contribution to Sahaj Sarathi Foundation. Your support strengthens our
              charitable work in education, healthcare, social security and community welfare across India.
              We are grateful for your trust and kindness.
            </p>
          </div>

          <div className="mt-6 rounded-md border border-gray-200 bg-navy-50/40 p-5" data-testid="receipt-panel">
            <dl className="space-y-2.5 text-sm">
              {[["Received with thanks from", r.donor_name],
                ["Address", [r.address, r.state, r.pin].filter(Boolean).join(", ") || "—"],
                ["PAN", r.pan_masked || "—"],
                ["Donation Purpose", r.purpose],
                ["Amount", `₹${Number(r.amount).toLocaleString("en-IN")}`],
                ["Amount in Words", r.amount_words],
                ["Payment Mode", r.payment_mode],
                ["Payment Reference", r.payment_ref || "—"],
                ["Payment Date", r.payment_date ? new Date(r.payment_date).toLocaleString("en-IN") : "—"]].map(([k, v]) => (
                <div key={k} className="flex flex-wrap justify-between gap-2 border-b border-dashed border-gray-200 pb-2 last:border-0">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-right font-semibold text-navy">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          {f.tax_80g && (
            <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-500">
              This donation is eligible for deduction under Section 80G of the Income Tax Act, 1961, as per the Foundation's registration ({f.tax_80g}), subject to applicable rules and the donor's own tax position.
            </p>
          )}
          <p className="mt-4 text-center text-[10px] text-slate-400">This is a computer-generated receipt from Sahaj Sarathi Foundation. Verify authenticity by scanning the QR code.</p>
        </div>

        <div className="no-print mt-6 flex flex-wrap justify-center gap-3">
          <a href={`/api/public/receipts/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noreferrer" className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="receipt-download"><Download size={14} /> Download PDF</a>
          <button onClick={() => window.print()} className="btn-dark !rounded-md !px-6 !py-2.5 text-sm" data-testid="receipt-print"><Printer size={14} /> Print</button>
          {r.has_email && (
            <button onClick={emailReceipt} disabled={emailBusy} className="btn-dark !rounded-md !px-6 !py-2.5 text-sm disabled:opacity-50" data-testid="receipt-email">
              {emailBusy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email Receipt
            </button>
          )}
          <Link to="/" className="btn-dark !rounded-md !px-6 !py-2.5 text-sm" data-testid="receipt-home"><Home size={14} /> Back to Home</Link>
        </div>
      </div>
    </div>
  );
}
